import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { Search, Eye, Download, FileText } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { PageHeader } from '../components/common/PageHeader';
import { EmptyState } from '../components/common/EmptyState';
import { PayslipModal } from '../components/payslips/PayslipModal';
import apiClient from '../services/apiClient';
import { selectUser } from '../store/authSlice';
import { formatINR } from '../lib/format';
import { downloadPayslip } from '../lib/downloadPayslip';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const fetchPaidRuns = () =>
  apiClient.get('/api/payroll/paid-with-results').then(r => r.data);

const buildSlips = (runs, businessName) => {
  const slips = [];
  (runs || []).forEach(run => {
    (run.employeeResults || []).forEach(r => {
      slips.push({
        runId:       run._id,
        empId:       r.employeeId,
        name:        r.employeeName,
        department:  r.department || '',
        type:        r.employmentType,
        month:       run.month,
        year:        run.year,
        monthLabel:  `${MONTHS[run.month - 1]} ${run.year}`,
        monthNum:    run.month,
        yearNum:     run.year,
        basic:       r.basicPay,
        hra:         r.hra,
        allowances:  r.allowances,
        overtime:    r.overtimePay,
        bonus:       r.bonus,
        pf:          r.pfDeduction,
        esi:         r.esiDeduction,
        lop:         r.lopDeduction,
        pt:          r.ptDeduction,
        gross:       r.grossEarnings,
        net:         r.netPay,
        daysPresent: r.daysPresent,
        halfDays:    r.halfDays,
        lopDays:     r.lopDays,
        businessName,
      });
    });
  });
  return slips;
};

export default function Payslips() {
  const user = useSelector(selectUser);
  const now  = new Date();

  const [filterMonth, setFilterMonth] = useState(String(now.getMonth() + 1));
  const [filterYear,  setFilterYear]  = useState(String(now.getFullYear()));
  const [search,      setSearch]      = useState('');
  const [deptFilter,  setDeptFilter]  = useState('all');
  const [selected,    setSelected]    = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['payrollRuns', 'paid-with-results'],
    queryFn:  fetchPaidRuns,
  });
  const runs    = data?.data || [];
  const allSlips = useMemo(() => buildSlips(runs, user?.businessName || 'SlipSense'), [runs, user]);

  const availableYears = useMemo(() =>
    [...new Set(allSlips.map(s => String(s.yearNum)))].sort((a,b) => b-a),
  [allSlips]);

  const availableDepts = useMemo(() =>
    [...new Set(allSlips.map(s => s.department).filter(Boolean))].sort(),
  [allSlips]);

  const filtered = useMemo(() => {
    return allSlips.filter(s => {
      if (filterMonth !== 'all' && s.monthNum !== Number(filterMonth)) return false;
      if (filterYear  !== 'all' && s.yearNum  !== Number(filterYear))  return false;
      if (deptFilter  !== 'all' && s.department !== deptFilter)         return false;
      if (search) {
        const q = search.toLowerCase();
        if (!s.name.toLowerCase().includes(q) && !s.department.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [allSlips, filterMonth, filterYear, deptFilter, search]);

  const totalNet   = filtered.reduce((s, r) => s + r.net, 0);
  const totalCount = filtered.length;

  const periodLabel = filterMonth !== 'all' && filterYear !== 'all'
    ? `${MONTHS[Number(filterMonth) - 1]} ${filterYear}`
    : filterYear !== 'all' ? filterYear : 'All time';

  return (
    <div>
      <PageHeader
        title="Payslips"
        description={`${totalCount} payslip${totalCount !== 1 ? 's' : ''} · ${periodLabel}`}
      />

      <Card className="p-4 mb-5 border-slate-200 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3 items-end">
          <div className="space-y-1 min-w-[130px]">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Month</Label>
            <Select value={filterMonth} onValueChange={setFilterMonth}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All months</SelectItem>
                {MONTHS.map((m, i) => (
                  <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1 min-w-[110px]">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Year</Label>
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All years</SelectItem>
                {availableYears.map(y => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1 min-w-[150px]">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Department</Label>
            <Select value={deptFilter} onValueChange={setDeptFilter}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All departments</SelectItem>
                {availableDepts.map(d => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1 space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Name or department…"
                className="pl-8 h-9"
              />
            </div>
          </div>

          {(filterMonth !== String(now.getMonth()+1) || filterYear !== String(now.getFullYear()) || deptFilter !== 'all' || search) && (
            <Button variant="outline" size="sm" className="h-9 shrink-0"
              onClick={() => {
                setFilterMonth(String(now.getMonth() + 1));
                setFilterYear(String(now.getFullYear()));
                setDeptFilter('all');
                setSearch('');
              }}>
              Reset
            </Button>
          )}
        </div>

        {filtered.length > 0 && (
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
            <span>{filtered.length} payslip{filtered.length !== 1 ? 's' : ''}</span>
            <span className="text-slate-300">|</span>
            <span>Total net: <span className="font-mono font-semibold text-[#2E6DA4]">{formatINR(totalNet)}</span></span>
          </div>
        )}
      </Card>

      <Card className="border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-10 text-center text-slate-400 text-sm">Loading payslips…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={FileText}
            title={allSlips.length === 0 ? 'No payslips yet' : 'No payslips match filters'}
            description={
              allSlips.length === 0
                ? 'Complete a payroll run (mark as paid) to generate payslips.'
                : 'Try changing the month, year, or department filter.'
            }
            actionLabel={allSlips.length === 0 ? 'Run Payroll' : undefined}
            onAction={allSlips.length === 0 ? () => window.location.href = '/payroll' : undefined}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {['Employee','Department','Month','Days','Gross','Deductions','Net Pay','Actions'].map(h => (
                    <th key={h} className={`px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider ${
                      ['Net Pay','Gross','Deductions','Actions'].includes(h) ? 'text-right' : 'text-left'
                    }`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((s, i) => (
                  <tr key={`${s.runId}-${s.empId}-${i}`} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#2E6DA4] to-[#1E3A5F] flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                          {s.name.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
                        </div>
                        <div className="text-sm font-medium text-slate-900">{s.name}</div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-500">{s.department || '—'}</td>
                    <td className="px-5 py-3.5 text-sm text-slate-700">{s.monthLabel}</td>
                    <td className="px-5 py-3.5 text-sm font-mono text-slate-600">
                      {s.daysPresent}P
                      {s.halfDays > 0 && <span className="text-amber-600 ml-1">{s.halfDays}H</span>}
                      {s.lopDays > 0  && <span className="text-red-600 ml-1">{s.lopDays}L</span>}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-right font-mono text-slate-700">{formatINR(s.gross)}</td>
                    <td className="px-5 py-3.5 text-sm text-right font-mono text-red-600">
                      −{formatINR((s.pf||0)+(s.esi||0)+(s.lop||0)+(s.pt||0))}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-right font-mono font-semibold text-[#2E6DA4]">
                      {formatINR(s.net)}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setSelected(s)} className="h-8">
                          <Eye className="w-3.5 h-3.5 mr-1" /> View
                        </Button>
                        <Button size="sm" variant="outline"
                          onClick={() => downloadPayslip(s)}
                          className="h-8">
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <PayslipModal slip={selected} onClose={() => setSelected(null)} />
    </div>
  );
}